import { ActivityPreview } from "@/components/ActivityFeed";
import { DetailLayout } from "@/components/DetailLayout";
import { EntityFiles } from "@/components/EntityFiles";
import { PluginSlot } from "@/components/plugin";
import { BundleItemsSection } from "@/components/product/BundleItemsSection";
import { CategoryTreePicker } from "@/components/product/CategoryTreePicker";
import { DimensionsRow } from "@/components/product/DimensionsRow";
import { LocalesSection } from "@/components/product/LocalesSection";
import { PropGrouped } from "@/components/product/PropGrouped";
import { SeoSection } from "@/components/product/SeoSection";
import {
	EnrichmentHistoryCard,
	PipelineRunsCard,
	QualityCard,
} from "@/components/product/SidebarCards";
import { StatusCard } from "@/components/product/StatusCard";
import { TagPicker } from "@/components/product/TagPicker";
import { VariantsSection } from "@/components/product/VariantsSection";
import { VersionBadge } from "@/components/product/VersionBadge";
import { VersionHistorySection } from "@/components/product/VersionHistorySection";
import {
	type PriceRow,
	type VendorRow,
	protoValueToString,
	slugMatchesKey,
	toSlug,
	useAutosave,
} from "@/components/product/helpers";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useWorkspacePage } from "@/components/workspace";
import { CatalogService } from "@/gen/omnios/v1/catalog_connect";
import type { Product, ProductCategory } from "@/gen/omnios/v1/catalog_pb";
import { MediaService } from "@/gen/omnios/v1/media_connect";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { cn } from "@/lib/utils";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	Check,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Cloud,
	Copy,
	CopyPlus,
	Loader2,
	Plus,
	Trash2,
	Upload,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/catalog/products/$id")({
	component: ProductDetailPage,
});

const client = createClient(CatalogService, transport);
const mediaClient = createClient(MediaService, transport);

function ProductDetailPage() {
	const { t } = useTranslation("products");
	const { id } = Route.useParams();
	const navigate = useNavigate();
	const qc = useQueryClient();

	const {
		data: product,
		isLoading,
		error,
	} = useQuery({
		queryKey: ["product", id],
		queryFn: () => client.getProduct({ id, includeVariants: true }),
	});

	const { data: categoriesResp } = useQuery({
		queryKey: ["categories", "*"],
		queryFn: () => client.listCategories({ parentId: "*" }),
	});
	const categories = categoriesResp?.categories ?? [];

	const categoryId = product?.categoryIds?.[0] ?? "";

	const { data: catProps } = useQuery({
		queryKey: ["category-properties", categoryId],
		queryFn: () =>
			client.listCategoryProperties({
				categoryId,
			}),
		enabled: !!categoryId,
	});

	const { data: allDefs } = useQuery({
		queryKey: ["property-definitions"],
		queryFn: () =>
			client.listPropertyDefinitions({
				categoryId: "",
			}),
	});

	const hasMedia = (product?.mediaIds ?? []).length > 0;
	const { data: mediaUrls } = useQuery({
		queryKey: ["media-urls", product?.mediaIds],
		queryFn: () =>
			mediaClient.batchGetMediaUrls({
				mediaIds: product?.mediaIds ?? [],
			}),
		enabled: hasMedia,
	});
	const imageUrls: {
		id: string;
		url: string;
	}[] = (mediaUrls?.urls ?? []).map((u) => ({
		id: u.mediaId,
		url: u.url,
	}));

	const deleteMut = useMutation({
		mutationFn: () => client.deleteProduct({ id }),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["products"],
			});
			qc.invalidateQueries({
				queryKey: ["home", "products"],
			});
			qc.invalidateQueries({
				queryKey: ["stats", "products"],
			});
			toast.success(t("delete_success"));
			navigate({ to: "/catalog/products", search: { owner_party_id: undefined } });
		},
		onError: () => toast.error(t("delete_error")),
	});

	if (isLoading) return <PageSkeleton />;

	if (error || !product) {
		return (
			<div className="flex items-center justify-center h-[60vh]">
				<div className="text-center">
					<h3 className="text-lg font-semibold mb-1">{t("error_loading")}</h3>
					<p className="text-sm text-muted-foreground">
						{error?.message ?? t("product_not_found")}
					</p>
					<Link
						to="/catalog/products"
						search={{ owner_party_id: undefined }}
						className="mt-4 inline-block text-sm text-primary underline"
					>
						{t("back_to_products")}
					</Link>
				</div>
			</div>
		);
	}

	return (
		<ProductEditor
			product={product}
			categories={categories}
			catProps={catProps?.definitions ?? []}
			allDefs={allDefs?.definitions ?? []}
			imageUrls={imageUrls}
			onDelete={() => deleteMut.mutate()}
			isDeleting={deleteMut.isPending}
		/>
	);
}

function PageSkeleton() {
	return (
		<DetailLayout
			sidebarWidth="lg:w-80"
			sidebar={
				<>
					<Card className="py-2.5 px-3">
						<Skeleton className="h-24 w-full rounded" />
					</Card>
					<Card className="py-2.5 px-3">
						<Skeleton className="h-20 w-full rounded" />
					</Card>
					<Card className="py-2.5 px-3">
						<Skeleton className="h-16 w-full rounded" />
					</Card>
				</>
			}
		>
			<Card className="space-y-4 px-3 py-2.5">
				<Skeleton className="h-10 w-2/3" />
				<Skeleton className="h-48 w-full rounded" />
				<Skeleton className="h-32 w-full rounded" />
			</Card>
			<Card className="space-y-3 px-3 py-2.5">
				<Skeleton className="h-6 w-24" />
				<Skeleton className="h-10 w-full" />
				<Skeleton className="h-10 w-full" />
			</Card>
		</DetailLayout>
	);
}

function centsToText(cents: string): string {
	if (!cents || cents === "0") return "";
	return (Number(cents) / 100).toFixed(2);
}

function textToCents(text: string): string {
	const trimmed = text.trim().replace(",", ".");
	if (!trimmed) return "0";
	const parsed = Number.parseFloat(trimmed);
	if (!Number.isFinite(parsed)) return "0";
	return String(Math.round(parsed * 100));
}

function PriceCentsInput({
	valueCents,
	onChangeCents,
	className,
}: {
	valueCents: string;
	onChangeCents: (cents: string) => void;
	className?: string;
}) {
	const [text, setText] = useState(() => centsToText(valueCents));
	const focusedRef = useRef(false);

	useEffect(() => {
		if (!focusedRef.current) {
			setText(centsToText(valueCents));
		}
	}, [valueCents]);

	const commit = () => {
		const cents = textToCents(text);
		if (cents !== valueCents) onChangeCents(cents);
		setText(centsToText(cents));
	};

	return (
		<Input
			type="text"
			inputMode="decimal"
			placeholder="0.00"
			value={text}
			onFocus={() => {
				focusedRef.current = true;
			}}
			onChange={(e) => setText(e.target.value.replace(/[^\d.,]/g, ""))}
			onBlur={() => {
				focusedRef.current = false;
				commit();
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter") (e.target as HTMLInputElement).blur();
			}}
			className={className}
		/>
	);
}

// ── Main editor ──

function ProductEditor({
	product,
	categories,
	catProps,
	allDefs,
	imageUrls,
	onDelete,
	isDeleting,
}: {
	product: Product;
	categories: ProductCategory[];
	catProps: {
		slug: string;
		name: string;
		unit?: string;
		valueType?: string;
		isFilterable?: boolean;
	}[];
	allDefs: {
		slug: string;
		name: string;
		unit?: string;
		valueType?: string;
		isFilterable?: boolean;
	}[];
	imageUrls: { id: string; url: string }[];
	onDelete: () => void;
	isDeleting: boolean;
}) {
	const { t } = useTranslation("products");
	const navigate = useNavigate();
	const qc = useQueryClient();
	const fileInputRef = useRef<HTMLInputElement>(null);

	useBreadcrumbs([
		{ label: t("breadcrumb_products"), href: "/catalog/products" },
		{ label: product.title || t("breadcrumb_product") },
	]);

	const [title, setTitle] = useState(product.title);
	const [slug, setSlug] = useState(product.slug);
	const [description, setDescription] = useState(product.description);
	const [isDraft, setIsDraft] = useState(product.isDraft);
	const [tagIds, setTagIds] = useState<string[]>(product.tagIds ?? []);
	const [selectedCats, setSelectedCats] = useState<string[]>(
		product.categoryIds,
	);

	// Build a map from category ID to category for parent lookups
	const categoryById = useMemo(
		() => new Map(categories.map((c) => [c.id, c])),
		[categories],
	);

	// Helper to build readable path by traversing parents
	const buildReadablePath = useCallback(
		(catId: string): string => {
			const parts: string[] = [];
			let current = categoryById.get(catId);
			while (current) {
				parts.unshift(current.name || current.slug || current.id);
				current = current.parentId
					? categoryById.get(current.parentId)
					: undefined;
			}
			return parts.join(" / ");
		},
		[categoryById],
	);
	const [localMediaIds, setLocalMediaIds] = useState<string[]>(
		product.mediaIds,
	);
	const [prices, setPrices] = useState<PriceRow[]>(
		product.pricing.map((p) => ({
			name: p.name,
			valueCents: String(p.valueCents),
			currency: p.currency || "RUB",
			isDefault: p.isDefault,
		})),
	);
	const [vendors, setVendors] = useState<VendorRow[]>(
		product.vendorCodes.map((v) => ({
			code: v.code,
			name: v.name,
			isDefault: v.isDefault,
		})),
	);

	const fields = product.properties?.fields ?? {};
	const [propValues, setPropValues] = useState<Record<string, string>>(() => {
		const out: Record<string, string> = {};
		for (const [k, v] of Object.entries(fields)) {
			out[k] = protoValueToString(v);
		}
		return out;
	});

	const [dims, setDims] = useState({
		heightMm: product.dimensions?.heightMm ?? 0,
		widthMm: product.dimensions?.widthMm ?? 0,
		lengthMm: product.dimensions?.lengthMm ?? 0,
		weightG: product.dimensions?.weightG ?? 0,
	});
	const [statusConfigId, setStatusConfigId] = useState(
		product.statusConfigId ?? "",
	);
	const [publishAtStr, setPublishAtStr] = useState(
		product.publishAt
			? new Date(Number(product.publishAt.seconds) * 1000)
					.toISOString()
					.slice(0, 16)
			: "",
	);
	const [unpublishAtStr, setUnpublishAtStr] = useState(
		product.unpublishAt
			? new Date(Number(product.unpublishAt.seconds) * 1000)
					.toISOString()
					.slice(0, 16)
			: "",
	);

	const [dirty, setDirty] = useState(false);
	const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
	const versionHistoryRef = useRef<HTMLDivElement>(null);

	// Wrap state setters to auto-mark dirty
	const editTitle = (v: string) => {
		setTitle(v);
		setDirty(true);
	};
	const editSlug = (v: string) => {
		setSlug(v);
		setDirty(true);
	};
	const editDescription = (v: string) => {
		setDescription(v);
		setDirty(true);
	};
	const editTagIds = (ids: string[]) => {
		setTagIds(ids);
		setDirty(true);
	};
	const editDraft = (v: boolean) => {
		setIsDraft(v);
		setDirty(true);
	};
	const editPrices = (fn: (p: PriceRow[]) => PriceRow[]) => {
		setPrices(fn);
		setDirty(true);
	};
	const editVendors = (fn: (v: VendorRow[]) => VendorRow[]) => {
		setVendors(fn);
		setDirty(true);
	};
	const editPropValues = (
		fn: (p: Record<string, string>) => Record<string, string>,
	) => {
		setPropValues(fn);
		setDirty(true);
	};
	const editCats = (v: string[]) => {
		setSelectedCats(v);
		setDirty(true);
	};
	const addCat = (v: string) => {
		setSelectedCats((sc) => [...sc, v]);
		setDirty(true);
	};
	const editDims = (d: typeof dims) => {
		setDims(d);
		setDirty(true);
	};
	const editStatusConfigId = (id: string, draft: boolean) => {
		setStatusConfigId(id);
		setIsDraft(draft);
		setDirty(true);
	};
	const editPublishAt = (v: string) => {
		setPublishAtStr(v);
		setDirty(true);
	};
	const editUnpublishAt = (v: string) => {
		setUnpublishAtStr(v);
		setDirty(true);
	};

	const uploadMut = useMutation({
		mutationFn: async (file: File) => {
			const buf = await file.arrayBuffer();
			return mediaClient.uploadMedia({
				filename: file.name,
				mimeType: file.type,
				data: new Uint8Array(buf),
				entityType: "product",
			});
		},
		onSuccess: (media) => {
			setLocalMediaIds((prev) => [...prev, media.id]);
			setDirty(true);
			qc.invalidateQueries({
				queryKey: ["media-urls"],
			});
			toast.success(t("upload_success"));
		},
		onError: () => toast.error(t("upload_error")),
	});

	const updateMut = useMutation({
		mutationFn: () => {
			const propertiesFields: Record<
				string,
				{
					kind: {
						case: string;
						value: unknown;
					};
				}
			> = {};
			for (const [k, v] of Object.entries(propValues)) {
				if (!v || v === "—") continue;
				const num = Number(v);
				if (!Number.isNaN(num) && v.trim()) {
					propertiesFields[k] = {
						kind: {
							case: "numberValue",
							value: num,
						},
					};
				} else {
					propertiesFields[k] = {
						kind: {
							case: "stringValue",
							value: v,
						},
					};
				}
			}

			return client.updateProduct({
				id: product.id,
				title,
				description,
				isDraft,
				pricing: prices.map((p) => ({
					name: p.name,
					valueCents: BigInt(p.valueCents || "0"),
					currency: p.currency,
					isDefault: p.isDefault,
				})),
				vendorCodes: vendors.map((v) => ({
					code: v.code,
					name: v.name,
					isDefault: v.isDefault,
				})),
				properties: {
					fields: propertiesFields as never,
				},
				categoryIds: selectedCats,
				mediaIds: localMediaIds,
				dimensions: dims,
				statusConfigId: statusConfigId || undefined,
			});
		},
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["product", product.id],
			});
			qc.invalidateQueries({
				queryKey: ["products"],
			});
			qc.invalidateQueries({
				queryKey: ["home", "products"],
			});
			qc.invalidateQueries({
				queryKey: ["stats", "products"],
			});
			setDirty(false);
		},
		onError: () => toast.error(t("save_error")),
	});

	const save = useCallback(
		() => updateMut.mutate(),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			title,
			slug,
			description,
			isDraft,
			tagIds,
			prices,
			vendors,
			propValues,
			selectedCats,
			localMediaIds,
			dims,
			statusConfigId,
		],
	);

	useAutosave(save, dirty);

	// Cmd+S / Ctrl+S to force save
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key === "s") {
				e.preventDefault();
				save();
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [save]);

	const [idCopied, setIdCopied] = useState(false);
	const copyId = useCallback(() => {
		navigator.clipboard.writeText(product.id);
		setIdCopied(true);
		setTimeout(() => setIdCopied(false), 1500);
	}, [product.id]);

	const duplicateMut = useMutation({
		mutationFn: () =>
			client.createProduct({
				title: `${title} (${t("copy_suffix")})`,
				description,
				isDraft: true,
				pricing: prices.map((p) => ({
					name: p.name,
					valueCents: BigInt(p.valueCents || "0"),
					currency: p.currency,
					isDefault: p.isDefault,
				})),
				vendorCodes: [],
				categoryIds: selectedCats,
			}),
		onSuccess: (res) => {
			qc.invalidateQueries({ queryKey: ["products"] });
			qc.invalidateQueries({ queryKey: ["home", "products"] });
			qc.invalidateQueries({ queryKey: ["stats", "products"] });
			toast.success(t("duplicate_success"));
			if (res.id)
				navigate({ to: "/catalog/products/$id", params: { id: res.id } });
		},
		onError: () => toast.error(t("duplicate_error")),
	});

	useWorkspacePage({});

	const allPropDefs = catProps.length > 0 ? catProps : allDefs;
	const defsBySlug = new Map(allDefs.map((d) => [d.slug, d]));
	const orderedSlugs = new Set(catProps.map((d) => d.slug));

	const sortedPropEntries = [
		...catProps
			.filter((d) => d.slug in fields || slugMatchesKey(d.slug, fields))
			.map((d) => ({
				key: d.name,
				slug: d.slug,
				def: d,
			})),
		...Object.keys(fields)
			.filter((key) => !orderedSlugs.has(toSlug(key)))
			.map((key) => {
				const s = toSlug(key);
				const def = defsBySlug.get(s);
				return {
					key: def?.name ?? key,
					slug: s,
					def,
				};
			}),
	];

	const sidebarContent = (
		<>
			{/* Status + lifecycle */}
			<StatusCard
				statusConfigId={statusConfigId}
				onStatusChange={editStatusConfigId}
				isDraft={isDraft}
				onDraftChange={editDraft}
				publishAt={publishAtStr}
				unpublishAt={unpublishAtStr}
				onPublishAtChange={editPublishAt}
				onUnpublishAtChange={editUnpublishAt}
			/>
			<div className="px-3 -mt-2">
				<SaveIndicator
					isPending={updateMut.isPending}
					isSuccess={updateMut.isSuccess}
					isError={updateMut.isError}
				/>
			</div>

			{/* Quality score + enrich */}
			<QualityCard
				productId={product.id}
				qualityScore={product.qualityScore}
				completeness={[
					{ key: "title", filled: !!title },
					{ key: "description", filled: !!description },
					{ key: "image", filled: localMediaIds.length > 0 },
					{ key: "category", filled: selectedCats.length > 0 },
					{ key: "price", filled: prices.length > 0 },
					{ key: "vendor_code", filled: vendors.length > 0 },
				]}
			/>

			{/* Categories */}
			<Card className="py-2.5 gap-1.5">
				<div className="px-3 space-y-2">
					<Label>{t("categories_label")}</Label>
					{selectedCats.length > 0 && (
						<div className="flex flex-wrap gap-1.5">
							{selectedCats.map((cid) => {
								const cat = categoryById.get(cid);
								const hasName = !!cat;
								const label = cat
									? cat.name || cat.slug || cid
									: cid.slice(0, 8) + "…";
								const fullPath = cat ? buildReadablePath(cid) : cid;
								return (
									<Tooltip key={cid}>
										<TooltipTrigger asChild>
											<Badge
												variant={hasName ? "secondary" : "outline"}
												className={cn(
													"gap-1 pr-1 cursor-pointer hover:bg-secondary/80",
													!hasName &&
														"text-muted-foreground font-mono text-[10px]",
												)}
												onClick={() =>
													navigate({
														to: "/catalog/products",
														search: { owner_party_id: undefined },
													})
												}
											>
												{label}
												<Button
													variant="ghost"
													size="icon"
													className="h-4 w-4 p-0 hover:text-destructive"
													onClick={(e) => {
														e.stopPropagation();
														editCats(selectedCats.filter((c) => c !== cid));
													}}
												>
													<X className="h-3 w-3" />
												</Button>
											</Badge>
										</TooltipTrigger>
										<TooltipContent>{fullPath}</TooltipContent>
									</Tooltip>
								);
							})}
						</div>
					)}
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								size="sm"
								className="h-8 text-sm w-full justify-start font-normal text-muted-foreground"
							>
								<Plus className="h-3.5 w-3.5 mr-1.5" />
								{t("add_category")}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-72 p-3" align="start">
							<CategoryTreePicker
								categories={categories}
								selected={selectedCats}
								onToggle={(id) => {
									if (selectedCats.includes(id)) {
										editCats(selectedCats.filter((c) => c !== id));
									} else {
										addCat(id);
									}
								}}
							/>
						</PopoverContent>
					</Popover>
				</div>
			</Card>

			{/* Tags */}
			<TagPicker tagIds={tagIds} onChange={editTagIds} />

			{/* Enrichment history */}
			<EnrichmentHistoryCard productId={product.id} />

			{/* Pipeline runs that produced this product */}
			<PipelineRunsCard entityType="product" entityId={product.id} />

			<EntityFiles entityType="product" entityId={product.id} compact />

			<Card className="py-2.5 gap-1.5">
				<div className="px-3">
					<ActivityPreview entityType="product" entityId={product.id} />
				</div>
			</Card>

			{/* Actions */}
			<Card className="py-2.5 gap-1.5">
				<div className="px-3 space-y-2.5">
					<Label>{t("actions_label")}</Label>
					<div className="space-y-2">
						<Button
							variant="outline"
							size="sm"
							className="w-full justify-start h-8 text-xs"
							onClick={copyId}
						>
							{idCopied ? (
								<>
									<Check className="h-3.5 w-3.5 mr-1.5 text-green-600" />
									{t("copied")}
								</>
							) : (
								<>
									<Copy className="h-3.5 w-3.5 mr-1.5" />
									{t("copy_id")}
								</>
							)}
						</Button>
						<Button
							variant="outline"
							size="sm"
							className="w-full justify-start h-8 text-xs"
							disabled={duplicateMut.isPending}
							onClick={() => duplicateMut.mutate()}
						>
							{duplicateMut.isPending ? (
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							) : (
								<CopyPlus className="h-3.5 w-3.5 mr-1.5" />
							)}
							{t("duplicate_product")}
						</Button>
						<AlertDialog>
							<AlertDialogTrigger asChild>
								<Button
									variant="outline"
									size="sm"
									disabled={isDeleting}
									className="w-full justify-start h-8 text-xs text-muted-foreground hover:text-destructive border-border hover:border-destructive/20 hover:bg-destructive/5"
								>
									<Trash2 className="h-3.5 w-3.5 mr-1.5" />
									{t("delete_product")}
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>
										{t("delete_product_confirm")}
									</AlertDialogTitle>
									<AlertDialogDescription>
										{t("delete_product_description", {
											title: product.title,
										})}
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
									<AlertDialogAction
										onClick={onDelete}
										className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
									>
										{t("common:delete")}
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</div>
					<div className="text-[10px] text-muted-foreground/40 font-mono break-all select-all">
						{product.id}
					</div>
				</div>
			</Card>
		</>
	);

	return (
		<DetailLayout sidebarWidth="lg:w-80" sidebar={sidebarContent}>

				{/* Variant banner */}
				{product.isVariant && product.parentId && (
					<div className="flex items-center gap-2 px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-sm">
						<Badge
							variant="outline"
							className="text-xs border-blue-300 dark:border-blue-700"
						>
							{t("variant_badge")}
						</Badge>
						<Link
							to="/catalog/products/$id"
							params={{ id: product.parentId }}
							className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
						>
							{t("go_to_parent")}
						</Link>
					</div>
				)}

				{/* Title + Media + Description */}
				<Card className="px-3 py-2.5 gap-3">
					<div>
						<Input
							value={title}
							onChange={(e) => editTitle(e.target.value)}
							className="text-xl font-semibold border-none shadow-none px-1 -mx-1 h-auto focus-visible:ring-0 rounded hover:bg-muted/50 focus:bg-muted/50 transition-colors"
							placeholder={t("product_title_placeholder")}
						/>
						<div className="flex items-center gap-1.5 mt-1 px-1">
							<span className="text-xs text-muted-foreground/50">/</span>
							<Input
								value={slug}
								onChange={(e) =>
									editSlug(
										e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
									)
								}
								className="h-5 text-xs text-muted-foreground border-none shadow-none px-0 focus-visible:ring-0 font-mono max-w-xs"
								placeholder={t("slug_placeholder")}
							/>
						</div>
					</div>

					<div className="flex gap-3 flex-wrap">
						{imageUrls.map((img, idx) => (
							<div key={img.id} className="relative group">
								{idx === 0 && imageUrls.length > 1 && (
									<span className="absolute top-1 left-1 bg-primary text-primary-foreground text-[9px] px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
										{t("main_image")}
									</span>
								)}
								<img
									src={img.url}
									alt=""
									role="button"
									tabIndex={0}
									className="h-28 w-28 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
									onClick={() => setLightboxIdx(idx)}
									onKeyDown={(e) => {
										if (e.key === "Enter") setLightboxIdx(idx);
									}}
								/>
								<div className="absolute -top-1.5 -right-1.5 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
									{idx > 0 && (
										<Button
											variant="outline"
											size="icon"
											className="rounded-full h-5 w-5 text-muted-foreground hover:text-foreground"
											onClick={() => {
												setLocalMediaIds((ids) => {
													const arr = [...ids];
													[arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
													return arr;
												});
												setDirty(true);
											}}
										>
											<ChevronLeft className="h-3 w-3" />
										</Button>
									)}
									<Button
										variant="destructive"
										size="icon"
										className="rounded-full h-5 w-5"
										onClick={() => {
											setLocalMediaIds((ids) =>
												ids.filter((i) => i !== img.id),
											);
											setDirty(true);
										}}
									>
										<X className="h-3 w-3" />
									</Button>
									{idx < imageUrls.length - 1 && (
										<Button
											variant="outline"
											size="icon"
											className="rounded-full h-5 w-5 text-muted-foreground hover:text-foreground"
											onClick={() => {
												setLocalMediaIds((ids) => {
													const arr = [...ids];
													[arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
													return arr;
												});
												setDirty(true);
											}}
										>
											<ChevronRight className="h-3 w-3" />
										</Button>
									)}
								</div>
							</div>
						))}
						<Button
							type="button"
							variant="outline"
							onClick={() => fileInputRef.current?.click()}
							disabled={uploadMut.isPending}
							className="h-28 w-28 rounded-lg border-dashed flex flex-col items-center justify-center text-muted-foreground/50 hover:text-muted-foreground hover:border-muted-foreground/40 cursor-pointer"
						>
							<Upload className="h-5 w-5 mb-1" />
							<span className="text-[11px]">
								{uploadMut.isPending ? t("upload_pending") : t("upload_photo")}
							</span>
						</Button>
						<input
							ref={fileInputRef}
							type="file"
							accept="image/*"
							className="hidden"
							onChange={(e) => {
								const file = e.target.files?.[0];
								if (file) uploadMut.mutate(file);
								e.target.value = "";
							}}
						/>
					</div>

					{/* Lightbox */}
					{lightboxIdx !== null && imageUrls[lightboxIdx] && (
						<div
							className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
							onClick={() => setLightboxIdx(null)}
							onKeyDown={(e) => {
								if (e.key === "Escape") setLightboxIdx(null);
								if (
									e.key === "ArrowRight" &&
									lightboxIdx < imageUrls.length - 1
								)
									setLightboxIdx(lightboxIdx + 1);
								if (e.key === "ArrowLeft" && lightboxIdx > 0)
									setLightboxIdx(lightboxIdx - 1);
							}}
							role="dialog"
							tabIndex={0}
						>
							<img
								src={imageUrls[lightboxIdx].url}
								alt=""
								className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
								onClick={(e) => e.stopPropagation()}
							/>
							{imageUrls.length > 1 && (
								<>
									{lightboxIdx > 0 && (
										<Button
											variant="ghost"
											size="icon"
											className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 text-white"
											onClick={(e) => {
												e.stopPropagation();
												setLightboxIdx(lightboxIdx - 1);
											}}
										>
											<ChevronLeft className="h-6 w-6" />
										</Button>
									)}
									{lightboxIdx < imageUrls.length - 1 && (
										<Button
											variant="ghost"
											size="icon"
											className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 text-white"
											onClick={(e) => {
												e.stopPropagation();
												setLightboxIdx(lightboxIdx + 1);
											}}
										>
											<ChevronRight className="h-6 w-6" />
										</Button>
									)}
								</>
							)}
							<Button
								variant="ghost"
								size="icon"
								className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-white/20 text-white"
								onClick={() => setLightboxIdx(null)}
							>
								<X className="h-5 w-5" />
							</Button>
							<div className="absolute bottom-4 text-white/60 text-sm">
								{lightboxIdx + 1} / {imageUrls.length}
							</div>
						</div>
					)}

					<div className="space-y-1.5">
						<Label>{t("description_label")}</Label>
						<Textarea
							value={description}
							onChange={(e) => editDescription(e.target.value)}
							rows={4}
							placeholder={t("description_placeholder")}
							className="resize-none"
						/>
					</div>

					<div className="space-y-1.5">
						<Label>{t("brand_label")}</Label>
						<Input
							value={propValues.brand ?? ""}
							onChange={(e) =>
								editPropValues((pv) => ({
									...pv,
									brand: e.target.value,
								}))
							}
							placeholder={t("brand_placeholder")}
							className="max-w-xs"
						/>
					</div>
				</Card>

				{/* Version badge */}
				<VersionBadge
					updatedAt={product.updatedAt}
					onShowHistory={() =>
						versionHistoryRef.current?.scrollIntoView({ behavior: "smooth" })
					}
				/>

				{/* Pricing */}
				<Card className="px-3 py-2.5 gap-2.5">
					<div className="flex items-center justify-between">
						<h3 className="text-sm font-medium">{t("prices_heading")}</h3>
						<Button
							size="sm"
							variant="ghost"
							className="h-7 text-xs"
							onClick={() =>
								editPrices((p) => [
									...p,
									{
										name: "",
										valueCents: "0",
										currency: "RUB",
										isDefault: p.length === 0,
									},
								])
							}
						>
							<Plus className="h-3 w-3 mr-1" />
							{t("common:add")}
						</Button>
					</div>
					{prices.length === 0 ? (
						<p className="text-sm text-muted-foreground">{t("no_prices")}</p>
					) : (
						<div className="space-y-2">
							{prices.map((p, i) => (
								<div key={i} className="flex items-center gap-2">
									<Input
										placeholder={t("price_name_placeholder")}
										value={p.name}
										onChange={(e) =>
											editPrices((pr) =>
												pr.map((x, j) =>
													j === i
														? {
																...x,
																name: e.target.value,
															}
														: x,
												),
											)
										}
										className="w-32 h-8 text-sm"
									/>
									<PriceCentsInput
										valueCents={p.valueCents}
										onChangeCents={(cents) =>
											editPrices((pr) =>
												pr.map((x, j) =>
													j === i ? { ...x, valueCents: cents } : x,
												),
											)
										}
										className="w-28 h-8 text-sm tabular-nums"
									/>
									<Select
										value={p.currency}
										onValueChange={(v) =>
											editPrices((pr) =>
												pr.map((x, j) =>
													j === i
														? {
																...x,
																currency: v,
															}
														: x,
												),
											)
										}
									>
										<SelectTrigger className="w-20 h-8 text-sm">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="RUB">₽</SelectItem>
											<SelectItem value="USD">$</SelectItem>
											<SelectItem value="EUR">€</SelectItem>
										</SelectContent>
									</Select>
									<Label className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
										<Checkbox
											checked={p.isDefault}
											onCheckedChange={() =>
												editPrices((pr) =>
													pr.map((x, j) => ({
														...x,
														isDefault: j === i,
													})),
												)
											}
										/>
										{t("is_default")}
									</Label>
									<Button
										size="sm"
										variant="ghost"
										className="text-muted-foreground hover:text-destructive h-8 w-8 p-0"
										onClick={() =>
											editPrices((pr) => pr.filter((_, j) => j !== i))
										}
									>
										<X className="h-3 w-3" />
									</Button>
								</div>
							))}
						</div>
					)}
				</Card>

				{/* Dimensions */}
				<DimensionsRow dims={dims} onChange={editDims} />

				{/* Vendor codes */}
				<Card className="px-3 py-2.5 gap-2.5">
					<div className="flex items-center justify-between">
						<h3 className="text-sm font-medium">{t("vendor_codes_heading")}</h3>
						<Button
							size="sm"
							variant="ghost"
							className="h-7 text-xs"
							onClick={() =>
								editVendors((v) => [
									...v,
									{
										code: "",
										name: "",
										isDefault: v.length === 0,
									},
								])
							}
						>
							<Plus className="h-3 w-3 mr-1" />
							{t("common:add")}
						</Button>
					</div>
					{vendors.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							{t("no_vendor_codes")}
						</p>
					) : (
						<div className="space-y-2">
							{vendors.map((v, i) => (
								<div key={i} className="flex items-center gap-2">
									<Input
										placeholder={t("vendor_code_placeholder")}
										value={v.code}
										onChange={(e) =>
											editVendors((vr) =>
												vr.map((x, j) =>
													j === i
														? {
																...x,
																code: e.target.value,
															}
														: x,
												),
											)
										}
										className="w-40 font-mono h-8 text-sm"
									/>
									<Input
										placeholder={t("vendor_name_placeholder")}
										value={v.name}
										onChange={(e) =>
											editVendors((vr) =>
												vr.map((x, j) =>
													j === i
														? {
																...x,
																name: e.target.value,
															}
														: x,
												),
											)
										}
										className="w-40 h-8 text-sm"
									/>
									<Label className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
										<Checkbox
											checked={v.isDefault}
											onCheckedChange={() =>
												editVendors((vr) =>
													vr.map((x, j) => ({
														...x,
														isDefault: j === i,
													})),
												)
											}
										/>
										{t("is_default")}
									</Label>
									<Button
										size="sm"
										variant="ghost"
										className="text-muted-foreground hover:text-destructive h-8 w-8 p-0"
										onClick={() =>
											editVendors((vr) => vr.filter((_, j) => j !== i))
										}
									>
										<X className="h-3 w-3" />
									</Button>
								</div>
							))}
						</div>
					)}
				</Card>

				{/* Properties (grouped by attribute groups) */}
				{(allPropDefs.length > 0 || sortedPropEntries.length > 0) && (
					<Card className="px-3 py-2.5 gap-2.5">
						<PropGrouped
							allPropDefs={allPropDefs}
							propValues={propValues}
							editPropValues={editPropValues}
						/>
					</Card>
				)}

				{/* Plugin extension point: product sidebar */}
				<PluginSlot
					slot="entity.product.sidebar"
					context={{
						workspaceId: product.workspaceId ?? "",
						entityId: product.id,
						entityType: "product",
					}}
					className="space-y-4"
				/>

				{/* Variants */}
				{!product.isVariant && (
					<VariantsSection productId={product.id} variants={product.variants} />
				)}

				{/* Bundle items (for bundle products) */}
				{product.isBundle && <BundleItemsSection productId={product.id} />}

				{/* ── Supplementary (collapsed by default) ── */}
				<Collapsible defaultOpen={false}>
					<CollapsibleTrigger className="flex items-center gap-2 pt-2 mt-2 border-t w-full group">
						<span className="text-[11px] text-muted-foreground/60 uppercase tracking-wider font-medium">
							{t("section_supplementary")}
						</span>
						<ChevronDown className="h-3 w-3 text-muted-foreground/40 transition-transform group-data-[state=open]:rotate-180" />
					</CollapsibleTrigger>
					<CollapsibleContent className="space-y-4 mt-3">
						<SeoSection
							productId={product.id}
							seoTitle={product.seoTitle}
							seoDescription={product.seoDescription}
						/>
						<LocalesSection productId={product.id} />
						<div ref={versionHistoryRef}>
							<VersionHistorySection productId={product.id} />
						</div>
					</CollapsibleContent>
				</Collapsible>
		</DetailLayout>
	);
}

// ── Save indicator ──

function SaveIndicator({
	isPending,
	isSuccess,
	isError,
}: {
	isPending: boolean;
	isSuccess: boolean;
	isError: boolean;
}) {
	const { t } = useTranslation("products");
	if (isPending) {
		return (
			<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
				<Loader2 className="h-3.5 w-3.5 animate-spin" />
				{t("saving")}
			</span>
		);
	}
	if (isError) {
		return (
			<span className="flex items-center gap-1.5 text-xs text-destructive">
				<X className="h-3.5 w-3.5" />
				{t("save_error")}
			</span>
		);
	}
	if (isSuccess) {
		return (
			<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
				<Cloud className="h-3.5 w-3.5" />
				{t("saved")}
			</span>
		);
	}
	return null;
}
